import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Chip,
    CircularProgress,
    Stack,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
    InputAdornment,
    IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import BlockIcon from "@mui/icons-material/Block";
import { API_ENDPOINTS, getData, postData } from "../../services/apiService.js";

/**
 * OWO-001 Phase 5 — Bank Accounts admin viewer.
 *
 * Lands on the Pending Review queue (OTP-confirmed destinations waiting
 * for an admin verdict, FIFO). Admin can switch filter to Verified /
 * Rejected / All, and search by owner name (free text — first name,
 * last name, or both).
 */

const STATUS_CHIP = {
    unverified: { label: "Unverified", color: "default" },
    pending: { label: "Pending OTP", color: "warning" },
    verified: { label: "Verified", color: "success" },
    failed: { label: "Rejected", color: "error" },
};

const STATUS_TABS = [
    { value: "pending", label: "Pending Review" },
    { value: "verified", label: "Verified" },
    { value: "failed", label: "Rejected" },
    { value: "all", label: "All" },
];

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
};

function BankAccounts() {
    const [statusFilter, setStatusFilter] = useState("pending");
    const [nameQuery, setNameQuery] = useState("");
    // Separate "applied" query so typing doesn't refetch on every keystroke;
    // user presses Enter or Search button to apply.
    const [appliedQuery, setAppliedQuery] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", text: "" });
    const [rejectDialog, setRejectDialog] = useState({ open: false, row: null, reason: "" });

    const showOk = (text) => setSnackbar({ open: true, severity: "success", text });
    const showErr = (e) =>
        setSnackbar({
            open: true,
            severity: "error",
            text: e?.response?.data?.message || e?.message || "Action failed",
        });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ status: statusFilter });
            if (appliedQuery) params.set("q", appliedQuery);
            const res = await getData(
                `${API_ENDPOINTS.payoutDestinationsAdminList}?${params.toString()}`
            );
            if (res?.statusCode === 200) {
                setRows(res.result?.destinations ?? []);
            } else {
                showErr({ message: res?.message });
                setRows([]);
            }
        } catch (e) {
            showErr(e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, appliedQuery]);

    // Auto-load on mount + whenever filter / applied-query changes.
    useEffect(() => {
        load();
    }, [load]);

    const applySearch = () => {
        setAppliedQuery(nameQuery.trim());
    };

    const clearSearch = () => {
        setNameQuery("");
        setAppliedQuery("");
    };

    const handleVerify = async (row) => {
        try {
            const url = API_ENDPOINTS.payoutDestinationAdminVerify.replace(":id", row.destination_id);
            const res = await postData(url, {});
            if (res?.statusCode === 200) {
                showOk(`Destination ${row.destination_id} verified.`);
                load();
            } else {
                showErr({ message: res?.message });
            }
        } catch (e) {
            showErr(e);
        }
    };

    const handleReject = async () => {
        const { row, reason } = rejectDialog;
        try {
            const url = API_ENDPOINTS.payoutDestinationAdminReject.replace(":id", row.destination_id);
            const res = await postData(url, { reason });
            if (res?.statusCode === 200) {
                showOk(`Destination ${row.destination_id} rejected.`);
                setRejectDialog({ open: false, row: null, reason: "" });
                load();
            } else {
                showErr({ message: res?.message });
            }
        } catch (e) {
            showErr(e);
        }
    };

    const T = { teal:"#007C84", tealDk:"#016268", tealLt:"#E0F4F5", emerald:"#059669", emeraldLt:"#D1FAE5", rose:"#E11D48", roseLt:"#FFE4E6", amber:"#D97706", amberLt:"#FEF3C7", bg:"#F7FAFC", border:"#E2E8F0", text:"#0F172A", muted:"#64748B" };

    return (
        <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>
            {/* Hero header */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3, boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Bank Accounts</Typography>
                <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.5 }}>
                    Owner payout destinations · Verify after OTP confirmation · Rejection is audit-logged
                </Typography>
            </Box>

            <Card sx={{ mb:3, borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)" }}>
                <CardContent>
                    <Stack direction={{ xs:"column", md:"row" }} spacing={2} alignItems={{ xs:"stretch", md:"center" }} justifyContent="space-between">
                        {/* Pill tab filter */}
                        <Box sx={{ display:"flex", gap:0.5, bgcolor:T.bg, border:`1px solid ${T.border}`, borderRadius:"12px", p:0.5, width:"fit-content" }}>
                            {STATUS_TABS.map(t => (
                                <Box key={t.value} onClick={() => setStatusFilter(t.value)} sx={{
                                    px:1.8, py:0.7, borderRadius:"9px", cursor:"pointer",
                                    bgcolor: statusFilter===t.value ? T.teal : "transparent",
                                    color:   statusFilter===t.value ? "#fff" : T.muted,
                                    fontWeight: statusFilter===t.value ? 700 : 500, fontSize:13,
                                    transition:"all 0.14s", "&:hover":{ bgcolor: statusFilter===t.value ? T.teal : T.tealLt, color: statusFilter===t.value ? "#fff" : T.teal },
                                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                                }}>{t.label}</Box>
                            ))}
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                label="Search by name"
                                placeholder="Owner first or last name"
                                size="small"
                                value={nameQuery}
                                onChange={(e) => setNameQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") applySearch();
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: nameQuery && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={clearSearch}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ minWidth: 260 }}
                            />
                            <Button
                                variant="contained"
                                onClick={applySearch}
                                disabled={loading}
                            >
                                Search
                            </Button>
                            <IconButton onClick={load} disabled={loading} title="Refresh">
                                <RefreshIcon />
                            </IconButton>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {loading
                                ? "Loading…"
                                : `${rows.length} destination${rows.length === 1 ? "" : "s"}` +
                                  ` (${STATUS_TABS.find((t) => t.value === statusFilter)?.label})`}
                            {appliedQuery && (
                                <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                                    matching "{appliedQuery}"
                                </Typography>
                            )}
                        </Typography>
                        {loading && <CircularProgress size={20} />}
                    </Stack>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:"12px" }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor:T.bg }}>
                                <TableRow sx={{ "& th":{ fontWeight:700, fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", py:1.5, borderBottom:`2px solid ${T.border}` } }}>
                                    <TableCell>Owner</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Beneficiary</TableCell>
                                    <TableCell>Bank / VPA</TableCell>
                                    <TableCell>Account / Address</TableCell>
                                    <TableCell>IFSC</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>OTP verified</TableCell>
                                    <TableCell>Created by</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <Typography color="text.secondary" py={3}>
                                                {statusFilter === "pending"
                                                    ? "Nothing waiting for review."
                                                    : "No destinations match."}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {rows.map((r) => {
                                    const chip = STATUS_CHIP[r.verification_status] || {
                                        label: r.verification_status,
                                        color: "default",
                                    };
                                    return (
                                        <TableRow key={r.destination_id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {r.owner_name || `Owner #${r.owner_id}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    #{r.owner_id}
                                                    {r.owner_mobile ? ` · ${r.owner_mobile}` : ""}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{r.type}</TableCell>
                                            <TableCell>{r.beneficiary_name || "—"}</TableCell>
                                            <TableCell>{r.bank_name || "—"}</TableCell>
                                            <TableCell>
                                                {r.type === "bank_account" ? r.account_masked : r.vpa_address}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: "monospace" }}>{r.ifsc_code || "—"}</TableCell>
                                            <TableCell>
                                                <Chip label={chip.label} color={chip.color} size="small" />
                                                {r.is_default && (
                                                    <Chip
                                                        label="Default"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ ml: 0.5 }}
                                                    />
                                                )}
                                                {!r.is_active && (
                                                    <Chip label="Inactive" size="small" sx={{ ml: 0.5 }} />
                                                )}
                                                {r.verification_failure_reason && (
                                                    <Typography variant="caption" color="error" display="block">
                                                        {r.verification_failure_reason}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{fmtDate(r.consent_otp_verified_at)}</TableCell>
                                            <TableCell>
                                                {r.created_by_admin_id ? `Admin ${r.created_by_admin_id}` : "Owner"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    {r.is_active &&
                                                        r.verification_status !== "verified" &&
                                                        r.consent_otp_verified_at && (
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<VerifiedIcon />}
                                                                onClick={() => handleVerify(r)}
                                                            >
                                                                Verify
                                                            </Button>
                                                        )}
                                                    {r.is_active && r.verification_status !== "failed" && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<BlockIcon />}
                                                            onClick={() => setRejectDialog({ open: true, row: r, reason: "" })}
                                                        >
                                                            Reject
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Dialog
                open={rejectDialog.open}
                onClose={() => setRejectDialog({ ...rejectDialog, open: false })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Reject destination — #{rejectDialog.row?.destination_id}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Owner will be notified of the rejection reason. Withdrawals against this destination will be blocked.
                    </Typography>
                    <TextField
                        label="Reason"
                        value={rejectDialog.reason}
                        onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="e.g. IFSC code does not match the bank name on file"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog({ ...rejectDialog, open: false })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleReject} disabled={!rejectDialog.reason?.trim()}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.text}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default BankAccounts;
